// Enhanced SaleHistory Component - FIXED for multi-round partial refunds and full-after-partial
// Key changes:
// 1) Allow multiple partial refunds on the same item (no forced "refund-all-remaining" after a prior partial).
// 2) Selection is tracked per *line item* (itemId + itemIndex) so duplicates of the same item are handled correctly.
// 3) Full-order refund after partial now reliably refunds only remaining available quantities (meta marks all as refunded) and sets amountRefunded to the order total.
// 4) UI fixes: single quantity input (removed duplicate), clear "Previously Refunded" indicator, robust clamping to available qty.

import React, { useEffect, useState, Component, useCallback, useRef, useMemo } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment/moment';
import { connect, useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import { AddToOrder, clearOrder } from '../../../../Redux/actions/orderEditAction';
import { clearCart } from '../../../../Redux/actions/cartAction';
import userManagement from '../../../services/userManagement';
import ApiService from '../../../services/apiService';
import { invokeBroadCastMessage } from '../../../services/signalRService';
import ReceiptPrintService from '../../../services/ReceiptPrintService';
import PasscodePopup from '../../../Cashier/components/PassCode/passCodeModal';
import PaymentComponent from '../../../Cashier/components/flocashPayment';

// ✅ Import tax actions + selectors (same as CashierBucket)
import { setTaxExplicit, setTaxImplicit } from '../../../../Redux/actions/taxActions';
import { getTaxExplicit, getTaxImplicit } from '../../../../Redux/selecters';
// ===== Money helpers (CENT-PRECISE) =====
const toCents = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100);
};
const fromCents = (c) => {
  const n = Number(c);
  if (!Number.isFinite(n)) return 0;
  return Number((n / 100).toFixed(2));
};
const clampCents = (v, min, max) => Math.min(max, Math.max(min, v));

// ✅ Payment amount helpers: distinguish NET APPLIED vs TENDERED
const getPaymentNetAmount = (p) => {
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // 1) Preferred "net-applied" fields
  const candidates = [p?.paymentAmount, p?.price, p?.amount, p?.amountCollected];
  for (const c of candidates) {
    const n = num(c);
    if (n > 0) return n;
  }

  // 2) Fallback: derive net from tendered amountPaid (common in POS)
  const paid = num(p?.amountPaid);
  if (paid > 0) {
    const change = num(p?.change);
    const net = Math.max(0, paid - change);
    return net;
  }

  return 0;
};

const getOrderNetPayable = (order) => {
  const total = Number(order?.orderAmount ?? 0);
  return Number.isFinite(total) ? Math.max(0, total) : 0;
};

const normalizeStr = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase();

const isRefundLine = (li) => normalizeStr(li?.kitchenInstructions).startsWith('refund');
const isVoidLine = (li) => normalizeStr(li?.kitchenInstructions).startsWith('void');

const itemSignature = (li) => {
  const id = li?.itemId ?? '';
  const price = Number(li?.menuItemPrice ?? 0).toFixed(2);
  const disc = Number(li?.menuItemDiscount ?? 0).toFixed(2);
  const discType = normalizeStr(li?.menuItemDiscountType ?? 'amount');
  return `${id}|${price}|${disc}|${discType}`;
};

const getBaseQtyForSignature = (orderItems, sig) => {
  const baseLines = orderItems.filter((x) => itemSignature(x) === sig && !x.refunded && !x.voidItem && !isRefundLine(x) && !isVoidLine(x));
  return baseLines.reduce((s, x) => s + Number(x.quantity || 0), 0);
};

const getAlreadyRefundedQtyForSignature = (orderItems, sig) => {
  return orderItems
    .filter((x) => itemSignature(x) === sig)
    .filter((x) => x.refunded || x.voidItem || isRefundLine(x) || isVoidLine(x))
    .reduce((s, x) => s + Number(x.quantity || 0), 0);
};

// ✅ Merge duplicate order rows — paymentAmount is SUM of all payment rows
const groupOrdersForGrid = (orders = []) => {
  const map = new Map();

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  for (const o of orders) {
    const key = o?.originalId ?? o?.orderId ?? o?.invoiceId ?? o?.saleId ?? o?.orderHeaderId ?? o?.id ?? o?.invoiceNumber;

    console.log('PAY ROW', {
      key,
      originalId: o?.originalId,
      orderId: o?.orderId,
      id: o?.id,
      invoice: o?.invoiceNumber,
      paid: o?.paymentAmount ?? o?.amountPaid ?? o?.amountCollected,
    });

    if (!key) continue;

    const method = o?.paymentMethod || null;
    const rowPaid = safeNum(o?.paymentAmount ?? o?.amountPaid ?? o?.amountCollected ?? 0);
    const existing = map.get(key);

    if (!existing) {
      map.set(key, {
        ...o,
        originalId: key,
        paymentAmount: rowPaid,
        paymentMethods: method ? [method] : [],
        payments: method
          ? [
              {
                method,
                paymentAmount: rowPaid,
                amountReturned: safeNum(o?.amountReturned ?? 0),
              },
            ]
          : [],
      });
    } else {
      if (new Date(o.orderDate) > new Date(existing.orderDate)) {
        existing.orderDate = o.orderDate;
      }

      existing.paymentAmount = safeNum(existing.paymentAmount) + rowPaid;

      if (method && !existing.paymentMethods.includes(method)) {
        existing.paymentMethods.push(method);
      }

      if (method) {
        const idx = existing.payments.findIndex((p) => p.method === method);
        if (idx >= 0) {
          existing.payments[idx].paymentAmount = safeNum(existing.payments[idx].paymentAmount) + rowPaid;
        } else {
          existing.payments.push({
            method,
            paymentAmount: rowPaid,
            amountReturned: safeNum(o?.amountReturned ?? 0),
          });
        }
      }
    }
  }

  return Array.from(map.values());
};

const normalizeOrderRow = (order) => {
  const oid = order?.originalId ?? order?.orderId ?? order?.invoiceId ?? order?.saleId ?? order?.orderHeaderId ?? order?.id;
  return {
    ...order,
    originalId: oid,
    id: oid,
    displayId: `ORD-${oid}`,
    orderAmount: Number(order?.orderAmount ?? 0),
    paymentAmount: Number(order?.paymentAmount ?? order?.amountPaid ?? order?.amountCollected ?? 0),
  };
};

const Order = ({ Currency }) => {
  const [orderData, setOrderData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ✅ Tax setting from Redux (same as CashierBucket)
  const taxImplicit = useSelector(getTaxImplicit);
  const taxExplicit = useSelector(getTaxExplicit);

  // ✅ Full refund payment selection + reference
  const [fullRefundPaymentMethod, setFullRefundPaymentMethod] = useState(1);
  const [fullRefundReferenceNo, setFullRefundReferenceNo] = useState('');

  const [loading, setLoading] = useState(false);
  const [status, setstatus] = useState('');
  const [selectedRows, setSelectedRows] = useState({});

  const [RefundModal, setRefundModalOpen] = useState(false);
  const [ItemRefundModal, setItemRefundModalOpen] = useState(false);

  const [PrintModal, setPrintModalOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  const [itemRefundLoading, setItemRefundLoading] = useState(false);

  const [formData, setFormData] = useState({ remarks: '' });

  const [itemRefundData, setItemRefundData] = useState({
    selectedItems: [],
    refundReason: '',
    replacementItems: [],
    isReplacement: false,
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasscodePopup, setShowPasscodePopup] = useState(false);
  const [refundType, setRefundType] = useState('order');

  const gridRef = useRef();
  const pendingRefundMethodRef = useRef(1);
  const pendingRefundReferenceRef = useRef('');
  // Payment related states
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState([]);

  // ✅ Item refund payment states
  const [refundPaymentMethod, setRefundPaymentMethod] = useState(1);
  const [refundReferenceNo, setRefundReferenceNo] = useState('');
  const [refundAmountCollected, setRefundAmountCollected] = useState(0);
  const [refundChange, setRefundChange] = useState(0);

  const [originalOrderPaymentMethod, setOriginalOrderPaymentMethod] = useState(null);
  const [originalTransactionId, setOriginalTransactionId] = useState(null);
  const [originalPaymentData, setOriginalPaymentData] = useState(null);
  const [fullRefundReason, setFullRefundReason] = useState('');
  const [fullRefundNotes, setFullRefundNotes] = useState('');

  // =====================================================
  // ✅ NEW: Fetch tax setting on mount (same as CashierBucket)
  // =====================================================
  useEffect(() => {
    fetchTaxSetting();
  }, []);

  const fetchTaxSetting = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_TAX_SETTING');
      const taxInclusive = Boolean(res?.data?.result?.taxInclusive);
      syncReduxTaxWithApiValue(taxInclusive);
    } catch (error) {
      console.error('Failed to fetch tax setting:', error);
    }
  };

  const syncReduxTaxWithApiValue = (taxInclusive) => {
    if (taxInclusive) {
      dispatch(setTaxImplicit(true));
      dispatch(setTaxExplicit(false));
    } else {
      dispatch(setTaxExplicit(true));
      dispatch(setTaxImplicit(false));
    }
  };

  // =====================================================

  const makeLineKey = (item) => itemSignature(item);

  const isReplacementLine = (li) => (li?.kitchenInstructions || '').toLowerCase().includes('replacement');

  const getLineUnitPrice = (li) => Number(li?.menuItemPrice ?? li?.itemAmount ?? 0);

  const getLineDiscountType = (li) => {
    const raw = String(li?.menuItemDiscountType ?? li?.MenuItemDiscountType ?? 'Amount')
      .trim()
      .toLowerCase();

    if (raw === '%' || raw.includes('percent')) return 'percentage';
    if (raw.includes('perc')) return 'percentage';
    return 'amount';
  };

  const getLineDiscountRaw = (li) => Number(li?.menuItemDiscount ?? li?.MenuItemDiscount ?? 0);

  const REFUND_REASONS = [
    { value: 'customer_return', label: 'Customer Return' },
    { value: 'damaged_item', label: 'Damaged Item' },
    { value: 'wrong_item', label: 'Wrong Item Sold' },
    { value: 'pricing_error', label: 'Pricing Error' },
    { value: 'duplicate_charge', label: 'Duplicate Charge' },
    { value: 'out_of_stock', label: 'Out of Stock' },
    { value: 'quality_issue', label: 'Quality Issue' },
    { value: 'other', label: 'Other' },
  ];

  const getLineDiscountPerUnit = (li) => {
    const raw = getLineDiscountRaw(li);
    if (!raw) return 0;

    const type = getLineDiscountType(li);
    if (type === 'percentage') {
      const unit = getLineUnitPrice(li);
      return (unit * raw) / 100;
    }
    return raw;
  };

  const getPreviouslyRefundedForItem = (itemId) => {
    const lines = (selectedRows?.orderItems || []).filter((o) => o.itemId === itemId);
    return lines.filter((o) => o.refunded || o.voidItem).reduce((s, o) => s + Number(o.quantity || 0), 0);
  };

  const getRefundHistoryText = (itemId) => {
    const totalRefunded = getPreviouslyRefundedForItem(itemId);
    if (totalRefunded === 0) return '';
    return `${totalRefunded} previously refunded`;
  };

  const hasPartialRefunds = (order) => {
    const items = order?.orderItems || [];
    const hasProcessed = items.some((x) => x.refunded || x.voidItem);
    const hasRemaining = items.some((x) => !x.refunded && !x.voidItem && Number(x.quantity || 0) > 0);
    return hasProcessed && hasRemaining;
  };

  const getRemainingQuantityForLine = (item) => {
    const items = selectedRows?.orderItems || [];
    if (!items.length) return 0;

    if (item.refunded || item.voidItem || isRefundLine(item) || isVoidLine(item)) return 0;

    const sig = itemSignature(item);
    const baseQty = getBaseQtyForSignature(items, sig);
    const refundedQty = getAlreadyRefundedQtyForSignature(items, sig);
    return Math.max(0, baseQty - refundedQty);
  };

  const getOriginalPaidAmount = (paymentData) => {
    const summed = Number(paymentData?.totalPaidAmount ?? 0);
    if (Number.isFinite(summed) && summed > 0) return summed;

    const net = getPaymentNetAmount(paymentData);
    return Number.isFinite(net) ? Math.max(0, net) : 0;
  };

  const getPaidBase = (order, paymentData) => {
    // ✅ Trust actual paid amount from payment rows first — never cap it
    const paidFromPayment = getOriginalPaidAmount(paymentData);
    if (paidFromPayment > 0) return paidFromPayment;

    // ✅ paymentAmount on order row (set during groupOrdersForGrid)
    const orderPaid = Number(order?.paymentAmount ?? 0);
    if (Number.isFinite(orderPaid) && orderPaid > 0) return orderPaid;

    // ✅ Last resort — orderAmount
    return getOrderNetPayable(order);
  };

  // ✅ outputs itemName + itemAmount to match UPDATE_ORDER_META schema
  const toMetaItem = (x) => ({
    itemId: x.itemId,
    itemName: x.menuItemName ?? x.itemName ?? '',
    itemAmount: Number(x.menuItemPrice ?? x.itemAmount ?? 0),
    menuItemTax: Number(x.menuItemTax ?? x.menuItemTaxAmount ?? 0),
    quantity: Number(x.quantity ?? 0),
    kitchenInstructions: x.kitchenInstructions ?? '',
    refunded: !!x.refunded,
    voidItem: !!x.voidItem,
    itemIndex: Number(x.itemIndex ?? 0),
    invoiceId: Number(x.invoiceId ?? 0),
    menuItemDiscount: Number(x.menuItemDiscount ?? x.MenuItemDiscount ?? 0),
    menuItemDiscountType: x.menuItemDiscountType ?? x.MenuItemDiscountType ?? 'Amount',
  });

  const GetPaymentMethods = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_PAYMENT_METHOD');
      const filteredMethods = res.data.result.filter((item) => item?.active === true);
      setPaymentMethod(filteredMethods);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const pickOriginalPaymentRow = (rows = []) => {
    if (!rows.length) return null;

    const nonRefund = rows
      .filter((p) => !p?.isRefund)
      .map((p) => ({ p, net: getPaymentNetAmount(p) }))
      .filter((x) => x.net > 0)
      .sort((a, b) => b.net - a.net);

    if (nonRefund[0]) return nonRefund[0].p;

    const any = rows
      .map((p) => ({ p, net: getPaymentNetAmount(p) }))
      .filter((x) => x.net > 0)
      .sort((a, b) => b.net - a.net);

    return any[0]?.p || rows[0] || null;
  };

  const getTotalPaidFromPaymentRows = (rows = []) => {
    return rows
      .filter((p) => !p?.isRefund)
      .map((p) => getPaymentNetAmount(p))
      .filter((v) => Number.isFinite(v) && v > 0)
      .reduce((s, v) => s + v, 0);
  };

  const getOriginalPaymentData = async (orderId, { setRefundMethod = true } = {}) => {
    try {
      const apiService = new ApiService();
      const res = await apiService.getApiParamater('GET_PAYMENT', `?orderId=${orderId}`);
      console.log('GET_PAYMENT API response', res);
      const rows = res?.data?.result || [];
      console.log('GET_PAYMENT rows sample', rows?.[0]);
      console.log(
        'NET calc per row',
        rows.map((r) => ({
          amountPaid: r.amountPaid,
          change: r.change,
          paymentAmount: r.paymentAmount,
          amount: r.amount,
          price: r.price,
          amountCollected: r.amountCollected,
          net: getPaymentNetAmount(r),
        })),
      );

      const totalPaid = getTotalPaidFromPaymentRows(rows);
      const paymentData = pickOriginalPaymentRow(rows);

      if (paymentData) {
        const enriched = { ...paymentData, totalPaidAmount: totalPaid };

        setOriginalPaymentData(enriched);
        setOriginalOrderPaymentMethod(paymentData.paymentMethodId || 1);

        if (setRefundMethod) {
          if (paymentData.paymentMethodId === 3) {
            setRefundPaymentMethod(1);
          } else {
            setRefundPaymentMethod(paymentData.paymentMethodId || 1);
          }
          setOriginalOrderPaymentMethod(paymentData.paymentMethodId || 1);
        }
        setOriginalTransactionId(paymentData.transactionId || null);

        console.log('REFUND DEBUG', { orderId, totalPaid, rows });

        return enriched;
      }

      const fallback = { totalPaidAmount: totalPaid };
      setOriginalPaymentData(fallback);
      if (setRefundMethod) {
        setOriginalOrderPaymentMethod(1);
        setRefundPaymentMethod(1);
      }
      return fallback;
    } catch (error) {
      console.error('Error fetching payment data:', error);
      if (setRefundMethod) {
        setOriginalOrderPaymentMethod(1);
        setRefundPaymentMethod(1);
      }
      return null;
    }
  };

  const GetMenuItems = async () => {
    try {
      setLoadingMenuItems(true);
      const apiService = new ApiService();
      const res = await apiService.get('GET_ITEM_MASTER');

      if (res?.data?.result) {
        const activeItems = res.data.result
          .filter((item) => item.itemDescription && item.salePrice > 0)
          .map((item) => ({
            value: item,
            label: `${item.itemDescription} - ${Currency?.currency?.code || ''}${Number(item.salePrice || 0).toFixed(2)}`,
            searchLabel: item.itemDescription?.toLowerCase() || '',
            itemName: item.itemDescription,
            itemDescription: item.itemDescription,
            recordId: item.recordId,
            record_Id: item.recordId,
            salePrice: item.salePrice,
            taxRate: item.vat ? Number(item.vat) : 0,
            vat: item.vat,
            ...item,
          }));

        setMenuItems(activeItems);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoadingMenuItems(false);
    }
  };

  const isRefundingAllRemaining = ({ order, selectedItems, replacementItems }) => {
    if (!order?.orderItems?.length) return false;
    if (!selectedItems?.length) return false;
    if (replacementItems?.length) return false;

    const remainingSaleLines = (order.orderItems || [])
      .filter((li) => !isReplacementLine(li))
      .filter((li) => !li.refunded && !li.voidItem)
      .filter((li) => !isRefundLine(li) && !isVoidLine(li));

    if (!remainingSaleLines.length) return false;

    const remainingBySig = new Map();
    for (const li of remainingSaleLines) {
      const sig = itemSignature(li);
      const qty = Number(li.quantity || 0);
      remainingBySig.set(sig, (remainingBySig.get(sig) || 0) + qty);
    }

    const selectedBySig = new Map();
    for (const s of selectedItems) {
      const sig = s.lineKey;
      selectedBySig.set(sig, (selectedBySig.get(sig) || 0) + Number(s.quantity || 0));
    }

    if (selectedBySig.size !== remainingBySig.size) return false;

    for (const [sig, remQty] of remainingBySig.entries()) {
      if ((selectedBySig.get(sig) || 0) !== remQty) return false;
    }

    return true;
  };

  // =====================================================
  // ✅ FIXED calcNetRefundAmount — now receives taxImplicit
  //    and applies tax multiplier when tax is explicit
  //    (menuItemPrice stored excl-tax but customer paid incl-tax)
  // =====================================================
  // REPLACE the entire calcNetRefundAmount function with this:
  const calcNetRefundAmount = ({ order, paymentData, selectedItems, replacementItems }) => {
    if (!order || !selectedItems?.length) return 0;

    const paidBaseC = toCents(getPaidBase(order, paymentData));
    const alreadyRefundedC = toCents(order?.amountRefunded || 0);
    const remainingPaidC = Math.max(0, paidBaseC - alreadyRefundedC);

    // If refunding all remaining items → return exact remaining paid
    if (isRefundingAllRemaining({ order, selectedItems, replacementItems })) {
      return fromCents(remainingPaidC);
    }

    // Partial refund: pro-rate by quantity selected vs total non-refunded quantity
    const allSaleLines = (order.orderItems || []).filter(
      (li) => !li.refunded && !li.voidItem && !isRefundLine(li) && !isVoidLine(li) && !isReplacementLine(li),
    );

    const totalRemainingQty = allSaleLines.reduce((s, li) => s + Number(li.quantity || 0), 0);
    if (totalRemainingQty <= 0) return 0;

    const selectedQty = selectedItems.reduce((s, sel) => s + Number(sel.quantity || 0), 0);
    const ratio = Math.min(1, selectedQty / totalRemainingQty);

    const replacementC = (replacementItems || []).reduce((tC, i) => {
      const priceC = toCents(i.selectedMenuItem?.salePrice || i.itemPrice || 0);
      return tC + priceC * Number(i.quantity || 0);
    }, 0);

    const proRatedC = Math.round(remainingPaidC * ratio);
    const netC = Math.max(0, proRatedC - replacementC);

    return fromCents(Math.min(netC, remainingPaidC));
  };
  // ✅ Wrapper — passes taxImplicit from Redux
  const calculateNetRefundAmount = () =>
    calcNetRefundAmount({
      order: selectedRows,
      paymentData: originalPaymentData,
      selectedItems: itemRefundData.selectedItems,
      replacementItems: itemRefundData.replacementItems,
    });

  const calculateItemRefundAmount = () => {
    if (!selectedRows?.orderItems) return 0;
    const linesByKey = new Map(selectedRows.orderItems.map((li) => [makeLineKey(li), li]));
    return itemRefundData.selectedItems.reduce((sum, sel) => {
      const li = linesByKey.get(sel.lineKey);
      if (!li) return sum;
      return sum + getLineUnitPrice(li) * Number(sel.quantity || 0);
    }, 0);
  };

  const calculateReplacementAmount = () =>
    itemRefundData.replacementItems.reduce((t, i) => t + Number(i.selectedMenuItem?.salePrice || i.itemPrice || 0) * Number(i.quantity || 0), 0);

  // ✅ Auto-fill cash refund when selections change
  useEffect(() => {
    const netRefundAmount = calcNetRefundAmount({
      order: selectedRows,
      paymentData: originalPaymentData,
      selectedItems: itemRefundData.selectedItems,
      replacementItems: itemRefundData.replacementItems,
      taxImplicit, // ✅ pass tax mode
    });

    if (netRefundAmount > 0) {
      if (refundPaymentMethod === 1) {
        setRefundAmountCollected(netRefundAmount);
        setRefundChange(0);
      } else {
        setRefundAmountCollected(netRefundAmount);
        setRefundChange(0);
      }
    }
  }, [itemRefundData.selectedItems, itemRefundData.replacementItems, refundPaymentMethod, originalPaymentData, selectedRows]);

  const handleRefundPaymentMethodChange = (methodId) => {
    setRefundPaymentMethod(methodId);
    setRefundReferenceNo('');

    if (methodId === 1) {
      const netRefundAmount = calculateNetRefundAmount();
      setRefundAmountCollected(netRefundAmount);
      setRefundChange(0);
    } else {
      setRefundAmountCollected(0);
      setRefundChange(0);
    }
  };

  const handleRefundAmountChange = (amount) => {
    const netRefundAmount = calculateNetRefundAmount();
    const parsed = parseFloat(amount) || 0;
    setRefundAmountCollected(parsed);
    setRefundChange(parsed > netRefundAmount ? Number((parsed - netRefundAmount).toFixed(2)) : 0);
  };

  // ✅ Create refund payment (item refund)
  const createRefundPayment = async (orderId, refundAmount, isPartial) => {
    try {
      const apiService = new ApiService();
      const selectedPaymentMethod = paymentMethod.find((p) => p.id === refundPaymentMethod);

      let paymentStatus = isPartial ? 9 : 3;
      let refundNotes = `Item refund via ${selectedPaymentMethod?.name || 'Cash'}`;

      if (refundReferenceNo) {
        refundNotes += ` | Ref: ${refundReferenceNo}`;
      }

      if (refundPaymentMethod === 3) {
        paymentStatus = 5;
        refundNotes = 'Flocash item refund initiated';
      }

      const refundPaymentBody = {
        orderId,
        paymentMethodId: refundPaymentMethod,
        paymentMethodName: selectedPaymentMethod?.name || 'Cash',

        amount: -refundAmount,
        paymentAmount: refundAmount,
        price: refundAmount,

        // ✅ FIXED: amountPaid = cash tendered (for cash), or exact refund (for non-cash)
        amountPaid: refundPaymentMethod === 1 ? (refundAmountCollected > 0 ? refundAmountCollected : refundAmount) : refundAmount,
        change: refundPaymentMethod === 1 ? Math.max(0, (refundAmountCollected > 0 ? refundAmountCollected : refundAmount) - refundAmount) : 0,

        paymentStatus,
        transactionId: `ITEM_REFUND_${Date.now()}`,
        paymentDate: new Date().toISOString(),
        currency: Currency?.currency?.code || '',
        customerId: selectedRows.customerId || 'Guest',
        customerName: selectedRows.customerName || 'Guest',
        notes: refundNotes,
        paymentType: refundPaymentMethod,
        isRefund: true,
        eftReference: refundReferenceNo || null,
      };

      const paymentRes = await apiService.post('CREATE_PAYMENT', refundPaymentBody);
      return paymentRes?.data?.statusCode === 201;
    } catch (error) {
      console.error('Refund payment creation failed:', error);
      return false;
    }
  };

  // ✅ Create full refund payment
  const createFullRefundPayment = async (orderId, refundAmount, methodId = fullRefundPaymentMethod, referenceNo = fullRefundReferenceNo) => {
    try {
      const apiService = new ApiService();
      const selectedPaymentMethod = paymentMethod.find((p) => p.id === methodId);

      let refundNotes = `Full refund via ${selectedPaymentMethod?.name || 'Cash'}`;
      if (referenceNo) {
        refundNotes += ` | Ref: ${referenceNo}`;
      }

      const refundPaymentBody = {
        orderId,
        paymentMethodId: methodId,
        paymentMethodName: selectedPaymentMethod?.name || 'Cash',
        amount: -refundAmount,
        paymentAmount: refundAmount,
        price: refundAmount,
        amountPaid: refundAmount,
        change: 0,
        paymentStatus: 3,
        transactionId: `FULL_REFUND_${Date.now()}`,
        paymentDate: new Date().toISOString(),
        currency: Currency?.currency?.code || '',
        customerId: selectedRows.customerId || 'Guest',
        customerName: selectedRows.customerName || 'Guest',
        notes: refundNotes,
        paymentType: methodId,
        isRefund: true,
        eftReference: referenceNo || null,
      };

      const paymentRes = await apiService.post('CREATE_PAYMENT', refundPaymentBody);
      return paymentRes?.data?.statusCode === 201;
    } catch (error) {
      console.error('Full refund payment creation failed:', error);
      return false;
    }
  };

  const handleItemRefund = async (orderData) => {
    const oid = orderData?.originalId ?? orderData?.id;
    if (!oid) {
      toast.warning('Please select an order first');
      return;
    }
    if (orderData.paymentStatus === 3) {
      toast.info('This order is already fully refunded');
      return;
    }
    if (orderData.paymentStatus === 0) {
      toast.info('Cannot refund pending orders');
      return;
    }

    try {
      const apiService = new ApiService();
      const res = await apiService.getApiParamater('GET_ORDERS', `?id=${oid}`);
      const latest = res?.data?.result?.[0];

      if (!latest) {
        toast.error('Could not fetch latest order data');
        return;
      }

      const normalized = normalizeOrderRow(latest);

      setSelectedRows(normalized);
      setItemRefundData({ selectedItems: [], refundReason: '', replacementItems: [], isReplacement: false });
      setRefundAmountCollected(0);
      setRefundChange(0);
      setRefundReferenceNo('');

      setOriginalPaymentData(null);
      setOriginalOrderPaymentMethod(null);
      setOriginalTransactionId(null);

      await getOriginalPaymentData(oid);

      setItemRefundModalOpen(true);
      if (menuItems.length === 0) GetMenuItems();
    } catch (e) {
      console.error(e);
      toast.error('Failed to load latest order for refund');
    }
  };

  const closeItemRefundModal = () => {
    setItemRefundModalOpen(false);
    setItemRefundData({ selectedItems: [], refundReason: '', replacementItems: [], isReplacement: false });
    setRefundAmountCollected(0);
    setRefundChange(0);
    setRefundReferenceNo('');
    setOriginalPaymentData(null);
    setOriginalOrderPaymentMethod(null);
    setOriginalTransactionId(null);
  };

  const openRefundModal = async () => {
    if (!selectedRows.originalId) {
      toast.warning('Please select an order first');
      return;
    }
    if (selectedRows.paymentStatus === 3) {
      toast.info('This order is already refunded');
      return;
    }
    if (selectedRows.paymentStatus === 0) {
      toast.info('Cannot refund pending orders');
      return;
    }
    if (hasPartialRefunds(selectedRows)) {
      toast.warning('This order has partial refunds. Please use Item Refund to refund remaining items.');
      return;
    }

    setFullRefundReason('');
    setFullRefundNotes('');
    setFullRefundReferenceNo('');

    const paymentData = await getOriginalPaymentData(selectedRows.originalId);

    const pm = paymentData?.paymentMethodId || 1;
    setFullRefundPaymentMethod(pm === 3 ? 1 : pm);

    setRefundModalOpen(true);
  };

  const closeRefundModal = () => {
    setRefundModalOpen(false);
    setFullRefundReason('');
    setFullRefundNotes('');
    setFullRefundReferenceNo('');
    // setFullRefundPaymentMethod(1);
    setFormData({ remarks: '' });
  };

  const handleItemSelection = (lineItem, isSelected) => {
    const lineKey = makeLineKey(lineItem);
    const maxQ = getRemainingQuantityForLine(lineItem);

    if (isSelected && maxQ === 0) {
      toast.warning('This item has no remaining quantity to refund');
      return;
    }

    setItemRefundData((prev) => {
      if (isSelected) {
        if (prev.selectedItems.some((x) => x.lineKey === lineKey)) return prev;
        return {
          ...prev,
          selectedItems: [
            ...prev.selectedItems,
            {
              lineKey,
              itemId: lineItem.itemId,
              itemIndex: lineItem.itemIndex ?? 0,
              quantity: Math.min(1, maxQ),
              maxQuantity: maxQ,
            },
          ],
        };
      } else {
        return { ...prev, selectedItems: prev.selectedItems.filter((si) => si.lineKey !== lineKey) };
      }
    });
  };

  const handleItemQuantityChange = (lineItem, newQuantity) => {
    const lineKey = makeLineKey(lineItem);
    const parsedQty = parseInt(newQuantity, 10);
    const remainingQty = getRemainingQuantityForLine(lineItem);

    if (!Number.isFinite(parsedQty) || parsedQty < 1) return;

    if (parsedQty > remainingQty) {
      toast.error(`Cannot refund more than ${remainingQty} (remaining quantity)`);
      return;
    }

    setItemRefundData((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.map((si) => (si.lineKey === lineKey ? { ...si, quantity: parsedQty, maxQuantity: remainingQty } : si)),
    }));
  };

  const isLineSelected = (lineItem) => itemRefundData.selectedItems.some((si) => si.lineKey === makeLineKey(lineItem));
  const getSelectedLineQuantity = (lineItem) => {
    const sel = itemRefundData.selectedItems.find((si) => si.lineKey === makeLineKey(lineItem));
    return sel ? sel.quantity : 1;
  };

  const handleSelectAllItems = () => {
    if (!selectedRows.orderItems) return;

    const allLines = selectedRows.orderItems
      .map((item) => {
        const remaining = getRemainingQuantityForLine(item);
        return {
          lineKey: makeLineKey(item),
          itemId: item.itemId,
          itemIndex: item.itemIndex ?? 0,
          quantity: remaining,
          maxQuantity: remaining,
        };
      })
      .filter((item) => item.maxQuantity > 0);

    if (allLines.length === 0) {
      toast.info('No items available to refund');
      return;
    }

    setItemRefundData((prev) => ({ ...prev, selectedItems: allLines }));
  };

  const handleDeselectAllItems = () => setItemRefundData((prev) => ({ ...prev, selectedItems: [] }));

  const handleReplacementToggle = (isReplacement) =>
    setItemRefundData((prev) => ({ ...prev, isReplacement, replacementItems: isReplacement ? prev.replacementItems : [] }));

  const addReplacementItem = () =>
    setItemRefundData((prev) => ({
      ...prev,
      replacementItems: [...prev.replacementItems, { id: Date.now(), selectedMenuItem: null, quantity: 1, notes: '' }],
    }));

  const updateReplacementItem = (id, field, value) => {
    setItemRefundData((prev) => ({
      ...prev,
      replacementItems: prev.replacementItems.map((item) => {
        if (item.id === id) {
          if (field === 'selectedMenuItem') {
            return {
              ...item,
              selectedMenuItem: value,
              itemName: value?.itemDescription || value?.itemName || '',
              itemPrice: Number(value?.salePrice || 0),
              itemId: value?.recordId || value?.record_Id || 0,
            };
          }
          return { ...item, [field]: value };
        }
        return item;
      }),
    }));
  };

  const removeReplacementItem = (id) => setItemRefundData((prev) => ({ ...prev, replacementItems: prev.replacementItems.filter((i) => i.id !== id) }));

  const executeItemRefund = async () => {
    if (!itemRefundData.selectedItems.length) {
      toast.warning('Please select at least one item to process');
      return;
    }

    const invalidSelections = itemRefundData.selectedItems.filter((sel) => {
      const item = selectedRows.orderItems.find((o) => makeLineKey(o) === sel.lineKey);
      if (!item) return true;
      const remaining = getRemainingQuantityForLine(item);
      return sel.quantity > remaining;
    });

    if (invalidSelections.length > 0) {
      toast.error('Some selected quantities exceed available quantities. Please check and try again.');
      return;
    }

    if (!itemRefundData.refundReason.trim()) {
      toast.warning(itemRefundData.isReplacement ? 'Please provide a replacement reason' : 'Please provide a refund reason');
      return;
    }

    if (itemRefundData.isReplacement && itemRefundData.replacementItems.length === 0) {
      toast.warning('Please add at least one replacement item');
      return;
    }

    if (itemRefundData.isReplacement) {
      const invalidItems = itemRefundData.replacementItems.some((item) => !item.selectedMenuItem || !item.selectedMenuItem.recordId || item.quantity <= 0);
      if (invalidItems) {
        toast.warning('Please select valid menu items and set quantities for all replacement items');
        return;
      }
    }

    const netRefundAmount = calculateNetRefundAmount();

    if (!itemRefundData.isReplacement && netRefundAmount > 0) {
      const selectedMethod = paymentMethod.find((p) => p.id === refundPaymentMethod);
      const methodName = (selectedMethod?.name || '').toLowerCase();

      if ((methodName.includes('eft') || methodName.includes('transfer') || methodName.includes('account')) && !refundReferenceNo.trim()) {
        toast.warning('Please provide a reference number for this refund method');
        return;
      }

      if (refundPaymentMethod === 1) {
        if (refundAmountCollected <= 0) {
          toast.warning('Please enter the cash amount given for refund');
          return;
        }
        if (refundAmountCollected < netRefundAmount) {
          toast.warning(`Cash amount must be at least ${netRefundAmount.toFixed(2)}`);
          return;
        }
      }
    }

    if (!isAuthenticated) {
      setRefundType('item');
      setItemRefundModalOpen(false);
      setShowPasscodePopup(true);
      return;
    }

    await processItemRefund();
  };

  const processItemRefund = async (flocashRefundCompleted = false) => {
    try {
      setItemRefundLoading(true);
      const apiService = new ApiService();

      const orderId = selectedRows?.originalId;
      if (!orderId) {
        toast.error('No order selected');
        return;
      }

      const latestRes = await apiService.getApiParamater('GET_ORDERS', `?id=${orderId}`);
      const latest = latestRes?.data?.result?.[0];
      if (!latest) {
        toast.error('Could not fetch latest order');
        return;
      }

      // STEP 1: Call the return API first
      const returnAPIResult = await callReturnAPI(selectedRows, itemRefundData.selectedItems);

      if (!returnAPIResult.success) {
        toast.error(`Return API failed: ${returnAPIResult.message}`);
        setItemRefundLoading(false);
        return;
      }

      const normalized = normalizeOrderRow(latest);
      const orderForMath = normalized;

      const paymentData = await getOriginalPaymentData(orderId, { setRefundMethod: false });

      // ✅ pass taxImplicit to calcNetRefundAmount
      const netRefundAmount = calcNetRefundAmount({
        order: orderForMath,
        paymentData,
        selectedItems: itemRefundData.selectedItems,
        replacementItems: itemRefundData.replacementItems,
      });

      const paidBaseC = toCents(getPaidBase(orderForMath, paymentData));
      const currentRefundedC = toCents(orderForMath.amountRefunded || 0);
      const requestRefundC = toCents(netRefundAmount || 0);

      const remainingPaidC = Math.max(0, paidBaseC - currentRefundedC);
      const newRefundC = clampCents(requestRefundC, 0, remainingPaidC);

      const totalRefundedC = Math.min(paidBaseC, currentRefundedC + newRefundC);

      const isFullyRefunded = totalRefundedC >= paidBaseC;
      const isPartiallyRefunded = totalRefundedC > 0 && !isFullyRefunded;

      const newPaymentStatus = isFullyRefunded ? 3 : isPartiallyRefunded ? 9 : orderForMath.paymentStatus;
      const newOrderStatus = isFullyRefunded ? 10 : isPartiallyRefunded ? 11 : orderForMath.orderStatus;

      setSelectedRows(normalized);

      const selectedMap = new Map(itemRefundData.selectedItems.map((s) => [s.lineKey, s]));
      const currentOrderItems = normalized.orderItems || [];
      let maxIndex = currentOrderItems.reduce((m, it) => Math.max(m, Number(it.itemIndex || 0)), 0);

      const updatedOrderItems = [];

      for (const item of currentOrderItems) {
        const lineKey = makeLineKey(item);
        const selectedLine = selectedMap.get(lineKey);

        if (item.refunded || item.voidItem) {
          updatedOrderItems.push(toMetaItem({ ...item, refunded: !!item.refunded, voidItem: !!item.voidItem }));
          continue;
        }

        if (!selectedLine) {
          updatedOrderItems.push(toMetaItem({ ...item, refunded: false, voidItem: false }));
          continue;
        }

        const qty = Number(item.quantity || 0);
        const refundQty = Math.min(Number(selectedLine.quantity || 0), qty);
        const remainingQty = Math.max(0, qty - refundQty);

        if (remainingQty > 0) {
          updatedOrderItems.push(toMetaItem({ ...item, quantity: remainingQty, refunded: false, voidItem: false }));
        }

        maxIndex += 1;
        updatedOrderItems.push(
          toMetaItem({
            ...item,
            quantity: refundQty,
            refunded: !itemRefundData.isReplacement,
            voidItem: !!itemRefundData.isReplacement,
            kitchenInstructions: `${itemRefundData.isReplacement ? 'VOID' : 'REFUND'} - ${item.kitchenInstructions || ''}`,
            itemIndex: maxIndex,
            invoiceId: 0,
          }),
        );
      }

      if (itemRefundData.isReplacement && itemRefundData.replacementItems.length > 0) {
        for (const r of itemRefundData.replacementItems) {
          const menuItem = r.selectedMenuItem;
          if (!menuItem) continue;

          maxIndex += 1;
          updatedOrderItems.push(
            toMetaItem({
              itemId: menuItem.recordId || menuItem.record_Id,
              itemName: menuItem.itemDescription || menuItem.itemName,
              itemPrice: Number(menuItem.salePrice || 0),
              menuItemTax: Number(menuItem.vat || menuItem.taxRate || 0),
              menuItemDiscount: 0,
              menuItemDiscountType: 'Amount',
              quantity: Number(r.quantity || 1),
              kitchenInstructions: r.notes || 'Replacement item',
              refunded: false,
              voidItem: false,
              itemIndex: maxIndex,
              invoiceId: 0,
            }),
          );
        }
      }

      const metaPayload = {
        id: parseInt(orderId, 10),
        orderItems: updatedOrderItems,
        orderItemsMeta: normalized.orderItemsMeta || [],
        loggedInUserId: userManagement.getUserName() || 'Guest',
      };

      const metaRes = await apiService.put('UPDATE_ORDER_META', metaPayload);
      const metaOk = metaRes?.data?.statusCode === 200 || metaRes?.status === 200;
      if (!metaOk) {
        toast.error('Failed to update order items meta.');
        return;
      }

      const headerPayload = {
        id: parseInt(orderId, 10),
        amountRefunded: fromCents(totalRefundedC),
        refundReason: itemRefundData.refundReason.trim(),
        paymentStatus: newPaymentStatus,
        orderStatus: newOrderStatus,
        loggedInUserId: userManagement.getUserName() || 'Guest',
      };

      const headerRes = await apiService.put('UPDATE_ORDER', headerPayload);
      const headerOk = headerRes?.data?.statusCode === 200 || headerRes?.status === 200;

      if (!headerOk) {
        toast.error('Failed to update order header.');
        return;
      }

      const newRefundAmount = fromCents(newRefundC);
      if (newRefundAmount > 0 && !flocashRefundCompleted) {
        await createRefundPayment(orderId, newRefundAmount, isPartiallyRefunded);
      }

      toast.success(itemRefundData.isReplacement ? 'Replacement processed successfully' : 'Refund processed successfully');

      // ✅ Auto-print using in-memory order data
      try {
        const printPaymentRes = await apiService.getApiParamater('GET_PAYMENT', `?orderId=${orderId}`);
        const printPaymentRows = printPaymentRes?.data?.result || [];
        const printPayments = printPaymentRows
          .filter((r) => !r?.isRefund && Number(r?.paymentAmount ?? r?.price ?? r?.amount ?? 0) > 0)
          .map((r) => ({
            method: r.paymentMethod || r.paymentMethodName || 'Payment',
            amount: Number(r.paymentAmount ?? r.price ?? r.amount ?? 0),
          }));

        const orderForPrint = {
          ...normalizeOrderRow(normalized),
          orderItems: updatedOrderItems,
          payments:
            printPayments.length > 0
              ? printPayments
              : normalized.payments || [
                  {
                    method: normalized.paymentMethod || 'Payment',
                    amount: Number(normalized.paymentAmount || normalized.orderAmount || 0),
                  },
                ],
        };

        const receiptData = buildReceiptDataFromOrder(orderForPrint, Currency?.currency);
        if (receiptData) {
          await ReceiptPrintService.printReceipt(receiptData, Currency?.currency);
        }
      } catch (printErr) {
        console.warn('Auto-print failed (non-blocking):', printErr);
      }

      setItemRefundModalOpen(false);
      setItemRefundData({ selectedItems: [], refundReason: '', replacementItems: [], isReplacement: false });
      setSelectedRows({});
      setRefundAmountCollected(0);
      setRefundChange(0);
      setRefundReferenceNo('');
      gridRef.current?.api?.deselectAll();
      GetOrders();
    } catch (err) {
      console.error(err);
      toast.error('Failed to process item refund.');
    } finally {
      setItemRefundLoading(false);
    }
  };

  const initiateUpdateOrder = () => {
    if (!fullRefundReason) {
      toast.warning('Please select a refund reason');
      return;
    }
    if (fullRefundReason === 'other' && !fullRefundNotes.trim()) {
      toast.warning('Please specify the refund reason');
      return;
    }

    const selectedMethod = paymentMethod.find((p) => p.id === fullRefundPaymentMethod);
    const methodName = (selectedMethod?.name || '').toLowerCase();

    if ((methodName.includes('eft') || methodName.includes('transfer') || methodName.includes('account')) && !fullRefundReferenceNo.trim()) {
      toast.warning('Please provide a reference number for this refund method');
      return;
    }

    // ✅ Save BEFORE closeRefundModal resets the state
    pendingRefundMethodRef.current = fullRefundPaymentMethod;
    pendingRefundReferenceRef.current = fullRefundReferenceNo;

    if (!isAuthenticated) {
      setRefundType('order');
      closeRefundModal();
      setShowPasscodePopup(true);
    } else {
      UpdateOrderData();
    }
  };
  // Return API
  const callReturnAPI = async (orderData, selectedItems) => {
    try {
      const normalizeSerials = (v) => {
        if (v == null) return [];
        if (Array.isArray(v)) {
          return v
            .map((x) => {
              if (x == null) return '';
              if (typeof x === 'object') return String(x.serialNumber ?? x.serial ?? x.value ?? x.number ?? '').trim();
              return String(x).trim();
            })
            .filter(Boolean);
        }
        if (typeof v === 'string') {
          return v
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        }
        if (typeof v === 'object') {
          const s = v.serialNumber ?? v.serial ?? v.value ?? v.number ?? '';
          return [String(s).trim()].filter(Boolean);
        }
        return [String(v).trim()].filter(Boolean);
      };

      const returnedItems = selectedItems.map((sel) => {
        // ✅ FIX: match using itemSignature, not itemId#itemIndex
        const line = orderData.orderItems.find((li) => itemSignature(li) === sel.lineKey);

        if (!line) {
          console.error(
            'Line not found for lineKey:',
            sel.lineKey,
            'Available signatures:',
            orderData.orderItems.map((li) => itemSignature(li)),
          );
          throw new Error(`Line not found for key: ${sel.lineKey}`);
        }

        const qty = Number(sel.quantity || 0);
        if (qty <= 0) throw new Error(`Invalid qty for ${sel.lineKey}`);

        const allSerials = normalizeSerials(line.itemSerialNumbers);

        if (allSerials.length > 0 && allSerials.length < qty) {
          throw new Error(`Not enough serials for ${line.menuItemName}. Need ${qty}, found ${allSerials.length}`);
        }

        return {
          material: line.itemCode || String(line.itemId),
          quantity: qty,
          itemSerialNumbers: allSerials.length > 0 ? allSerials.slice(0, qty).join(',') : null,
        };
      });

      const returnAPIBody = {
        endpointUrl: 'https://tp-apg-testza.multichoice.co.za/ws/SalesProcessingConfirmationAPI.HTTP_Port/1.0',
        templateName: 'SubscriberReturn',
        parameters: {
          countryIdentifier: 'zaf',
          referenceID: orderData.referenceID || orderData.originalId.toString(),
          orderReason: '912',
          returnedItemList: returnedItems,
        },
        token: 'asfsdfsdfsfsddfsdfsdf',
      };

      const apiService = new ApiService();
      const response = await apiService.post('CREATE_SOAP_SALE_RETURN', returnAPIBody);

      if (response?.data?.statusCode === 201 || response?.data?.statusCode === 200 || response?.statusCode === 200) {
        return { success: true, data: response.data };
      } else if (response?.data?.statusCode === 417) {
        return { success: false, message: response?.data?.message || 'Return API call failed' };
      } else {
        return { success: false, message: response?.data?.message || 'Return API call failed' };
      }
    } catch (error) {
      console.error('Error calling return API:', error);
      return { success: false, message: error.message || 'Return API call failed' };
    }
  };

  const UpdateOrderData = async () => {
    const capturedRefundMethod = pendingRefundMethodRef.current;
    const capturedRefundReference = pendingRefundReferenceRef.current;

    try {
      setLoading(true);

      const orderId = selectedRows?.originalId;
      if (!orderId) {
        toast.error('No order selected.');
        return;
      }

      if (hasPartialRefunds(selectedRows)) {
        toast.warning('Cannot perform full refund on an order with partial refunds.');
        return;
      }

      const apiService = new ApiService();
      const latestRes = await apiService.getApiParamater('GET_ORDERS', `?id=${orderId}`);
      const latest = latestRes?.data?.result?.[0];
      if (!latest) {
        toast.error('Could not fetch latest order');
        return;
      }

      const normalized = normalizeOrderRow(latest);

      // ✅ Build selectedItems for ALL non-refunded lines for the return API
      const allActiveItems = (normalized.orderItems || [])
        .filter((li) => !li.refunded && !li.voidItem && !isRefundLine(li) && !isVoidLine(li))
        .map((li) => ({
          lineKey: itemSignature(li),
          itemId: li.itemId,
          itemIndex: li.itemIndex ?? 0,
          quantity: Number(li.quantity || 0),
          maxQuantity: Number(li.quantity || 0),
        }));

      // ✅ Call return API with all active items
      const returnAPIResult = await callReturnAPI(normalized, allActiveItems);

      if (!returnAPIResult.success) {
        toast.error(`Return API failed: ${returnAPIResult.message}`);
        setLoading(false);
        return;
      }

      setSelectedRows(normalized);

      const fullyRefundedOrderItems = (normalized?.orderItems || []).map((it) => toMetaItem({ ...it, refunded: !isReplacementLine(it), voidItem: false }));

      const updateOrderMetaPayload = {
        id: parseInt(orderId, 10),
        orderItems: fullyRefundedOrderItems,
        orderItemsMeta: normalized?.orderItemsMeta || [],
        loggedInUserId: userManagement.getUserName() || 'Guest',
      };

      const updateMetaRes = await apiService.put('UPDATE_ORDER_META', updateOrderMetaPayload);
      const metaOk = updateMetaRes?.data?.statusCode === 200 || updateMetaRes?.status === 200;
      if (!metaOk) {
        toast.error('Failed to update order items (meta).');
        return;
      }

      const paymentData = await getOriginalPaymentData(orderId, { setRefundMethod: false });

      const paidBaseC = toCents(getPaidBase(normalized, paymentData));
      const alreadyRefundedC = toCents(normalized.amountRefunded || 0);
      const refundNowC = Math.max(0, paidBaseC - alreadyRefundedC);
      const refundNow = fromCents(refundNowC);

      if (refundNow > 0) {
        await createFullRefundPayment(orderId, refundNow, capturedRefundMethod, capturedRefundReference);
      }

      const headerPayload = {
        id: parseInt(orderId, 10),
        paymentStatus: 3,
        orderStatus: 10,
        amountRefunded: fromCents(paidBaseC),
        refundReason: fullRefundReason === 'other' ? fullRefundNotes.trim() : fullRefundReason,
        loggedInUserId: userManagement.getUserName() || 'Guest',
      };

      const res = await apiService.put('UPDATE_ORDER', headerPayload);

      if (res?.data?.statusCode === 200 || res?.status === 200) {
        toast.success('Order Refunded Successfully');
        invokeBroadCastMessage(`Order Refunded By Cashier. OrderNo#${orderId}`);
        setRefundModalOpen(false);
        setFormData({ remarks: '' });
        setSelectedRows({});
        setFullRefundPaymentMethod(1);
        setFullRefundReferenceNo('');
        gridRef.current?.api?.deselectAll();
        GetOrders();
      } else {
        toast.error('Failed to process refund.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to process refund.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasscodeSubmit = (isVerified) => {
    if (isVerified) {
      setIsAuthenticated(true);
      setShowPasscodePopup(false);
      if (refundType === 'order') {
        UpdateOrderData();
      } else if (refundType === 'item') {
        processItemRefund();
      }
    }
  };

  const closePasscodePopup = () => {
    setShowPasscodePopup(false);
    setIsAuthenticated(false);
    setRefundType('');
  };

  useEffect(() => {
    GetOrders();
    GetPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const GetOrders = async () => {
    try {
      setLoading(true);
      const apiService = new ApiService();
      const parameters = status ? `?OrderStatus=${status}` : '';
      const res = await apiService.getApiParamater('GET_ORDERS', parameters);

      if (Array.isArray(res?.data?.result)) {
        const filtered = res.data.result.filter((order) => order?.orderStatus !== 8);
        const grouped = groupOrdersForGrid(filtered);
        grouped.reverse();
        setOrderData(grouped);
      } else {
        setOrderData([]);
      }
    } catch (error) {
      console.error('Error fetching Orders:', error);
      toast.error('Failed to fetch orders');
      setOrderData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = (data) => {
    dispatch(clearOrder());
    dispatch(clearCart());

    const orderItems = data?.orderItems || [];
    const orderItemsMeta = data?.orderItemsMeta || [];

    const orderItemsMetaMap = {};
    orderItemsMeta.forEach((orderItemMeta) => {
      const menuItemId = orderItemMeta.menuItemId;
      if (!orderItemsMetaMap[menuItemId]) {
        orderItemsMetaMap[menuItemId] = [];
      }
      orderItemsMetaMap[menuItemId].push(orderItemMeta);
    });

    orderItems.forEach((orderItem, index) => {
      const menuItemId = orderItem.itemId;
      const itemMeta = orderItemsMetaMap[menuItemId] || [];
      const selectedVariant = itemMeta.find((meta) => meta.type === 4) || null;

      const processedItem = {
        itemData: {
          ...orderItem,
          id: orderItem.itemId,
          itemId: orderItem.itemId,
          menuItemName: orderItem.menuItemName || orderItem?.itemName || orderItem?.itemDescription,
          menuItemPrice: orderItem.menuItemPrice,
          orderItemsMeta: itemMeta,
          itemIndex: orderItem.itemIndex || index,
          voidItem: orderItem.voidItem || false,
          refunded: orderItem.refunded || false,
          menuItemDiscount: Number(orderItem.menuItemDiscount || 0),
        },
        quantity: orderItem.quantity || 1,
        specialInstructions: orderItem.kitchenInstructions || '',
        selectedVariant: selectedVariant,
        selectedAdOn: [],
        selectedModifier: [],
        void: orderItem.voidItem || false,
        refund: orderItem.refunded || false,
        index: index,
      };
      dispatch(AddToOrder(processedItem));
    });
  };

  const handleShow = (field, data) => {
    if (data.paymentStatus === 0) {
      handleEditOrder(data);
      navigate('/admin/UpdateOrder', { state: data });
    } else {
      navigate(`/admin/orderDetails/${field}`, { state: data });
    }
  };

  const handlePrintReceipt = async (orderData) => {
    try {
      const apiService = new ApiService();
      const orderId = orderData.originalId ?? orderData.id;

      const res = await apiService.getApiParamater('GET_ORDERS', `?id=${orderId}`);
      const rows = res?.data?.result;

      if (!rows?.length) {
        toast.error('Could not fetch latest order data');
        return;
      }

      const payments = rows
        .filter((r) => r?.paymentMethod && Number(r?.paymentAmount ?? 0) > 0)
        .map((r) => ({
          method: r.paymentMethod,
          amount: Number(r.paymentAmount),
        }));

      const baseOrder = normalizeOrderRow(rows[0]);

      const orderWithPayments = {
        ...baseOrder,
        payments,
        paymentMethods: payments.map((p) => p.method),
      };

      setSelectedRows(orderWithPayments);
      setPrintModalOpen(true);
    } catch (error) {
      console.error('Error fetching latest order data:', error);
      toast.error('Error fetching order data for printing');
    }
  };

  const buildReceiptDataFromOrder = (order, currency) => {
    if (!order) return null;

    const allItems = order.orderItems || [];
    const isReplacement = (item) => (item.kitchenInstructions || '').toLowerCase().includes('replacement');

    const activeItems = allItems.filter((item) => !item.refunded && !item.voidItem);
    const refundedItems = allItems.filter((item) => item.refunded || item.voidItem);

    const orderItems = activeItems.map((item) => {
      const isRepl = isReplacement(item);
      return {
        itemName: item.menuItemName || item.itemName || item.itemDescription || `Item ${item.itemId}`,
        quantity: Number(item.quantity || 0),
        menuItemPrice: Number(item.menuItemPrice || item.itemAmount || 0),
        selectedVariant: item.selectedVariant || '',
        specialInstructions: isRepl ? '🔄 Replacement Item' : item.kitchenInstructions || item.specialInstructions || '',
        isReplacement: isRepl,
      };
    });

    const itemsTotal = activeItems.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const price = Number(item.menuItemPrice || item.itemAmount || 0);
      const discRaw = Number(item.menuItemDiscount || 0);
      const discType = String(item.menuItemDiscountType || 'amount').toLowerCase();
      const discPU = discType.includes('percent') ? (price * discRaw) / 100 : discRaw;
      return sum + Math.max(0, price - discPU) * qty;
    }, 0);

    const tax = Number(order.taxAmount || order.tax || 0);
    const discount = Number(order.discountAmount || order.discount || 0);
    const total = itemsTotal > 0 ? itemsTotal : Number(order.orderAmount || 0);
    const subtotal = total + discount - tax;

    const orderTypeMap = {
      0: 'Dine-In',
      1: 'Take-Away',
      2: 'Delivery',
      3: 'Online Order',
      4: 'Drive Through',
      5: 'Catering',
      6: 'Room Service',
      7: 'Pickup',
      8: 'Curbside Pickup',
      9: 'Pre-Order',
      10: 'Subscription',
    };

    const payments =
      Array.isArray(order.payments) && order.payments.length > 0
        ? order.payments.filter((p) => p?.method && Number(p?.amount ?? 0) > 0)
        : (() => {
            const method = order.paymentMethod || order.paymentMethodName;
            const amount = Number(order.paymentAmount ?? 0);
            return method && amount > 0 ? [{ method, amount }] : [];
          })();

    const amountPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0) || Number(order.paymentAmount ?? total);
    const change = Math.max(0, amountPaid - total);

    return {
      orderId: order.originalId || order.id,
      orderDate: order.orderDate,
      orderItems,
      subtotal: subtotal > 0 ? subtotal : total,
      tax,
      discount,
      total,
      payments,
      amountPaid,
      change,
      customerName: order.customerName || 'Walk-in Customer',
      orderType: orderTypeMap[Number(order.orderType)] || 'Unknown',
      invoiceNumber: order.invoiceNumber || `INV-${order.originalId || order.id}`,
      paymentMethod: payments.map((p) => p.method).join(' + ') || 'N/A',
      tableNumber: order.tableNumber || '',
      refundedItems: refundedItems.map((item) => ({
        itemName: item.menuItemName || `Item ${item.itemId}`,
        quantity: Number(item.quantity || 0),
        menuItemPrice: Number(item.menuItemPrice || 0),
      })),
    };
  };

  const executeBrowserPrint = async () => {
    if (!selectedRows?.id) {
      toast.error('No order selected for printing');
      return;
    }

    try {
      setPrintLoading(true);
      const receiptData = buildReceiptDataFromOrder(selectedRows, Currency?.currency);
      if (!receiptData) {
        toast.error('Could not build receipt data for printing');
        return;
      }
      await ReceiptPrintService.printReceipt(receiptData, Currency?.currency);
      toast.success('Receipt sent to browser print dialog');
    } catch (error) {
      console.error('Browser print error:', error);
      toast.error('Failed to print via browser');
    } finally {
      setPrintLoading(false);
    }
  };

  const closePrintModal = () => {
    setPrintModalOpen(false);
    setSelectedRows({});
  };

  const onSelectionChanged = useCallback(() => {
    if (!gridRef.current) return;
    const sel = gridRef.current.api.getSelectedRows();
    if (sel.length > 0) {
      setSelectedRows(sel[0]);
    } else {
      setSelectedRows({});
    }
  }, []);

  const PaymentMethodsCellRenderer = (props) => {
    const methods = props.data?.paymentMethods || (props.value ? [props.value] : []);
    if (!methods.length) {
      return <span style={{ background: '#e2e3e5', color: '#6c757d', padding: '6px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 700 }}>N/A</span>;
    }
    return (
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {methods.map((m) => (
          <span
            key={m}
            style={{
              background: '#e2e3e5',
              color: '#495057',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '11px',
              fontWeight: 700,
              border: '1px solid #adb5bd',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {m}
          </span>
        ))}
      </div>
    );
  };

  const dateFormatter = (params) => {
    const date = moment(params.value);
    return date.isValid() ? date.format('MMM DD, YYYY HH:mm') : '';
  };
  const currencyFormatter = (params) => Number(params.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const StatusCellRenderer = useCallback((props) => {
    const { value } = props;
    const field = props.colDef.field;

    const paymentStatusMap = {
      0: { text: 'Pending', color: '#856404', bg: '#fff3cd' },
      1: { text: 'Authorized', color: '#6E5199', bg: '#E8E2FF' },
      2: { text: 'Paid', color: '#28a745', bg: '#d4edda' },
      3: { text: 'Refunded', color: '#dc3545', bg: '#f8d7da' },
      9: { text: 'Partially Refunded', color: '#fd7e14', bg: '#ffeaa7' },
    };

    const orderStatusMap = {
      6: { text: 'Completed', color: '#28a745', bg: '#d4edda' },
      8: { text: 'On-Hold', color: '#6c757d', bg: '#e2e3e5' },
      10: { text: 'Refunded', color: '#dc3545', bg: '#f8d7da' },
      11: { text: 'Partially Refunded', color: '#fd7e14', bg: '#ffeaa7' },
    };

    let s = { text: 'Unknown', color: '#6c757d', bg: '#e2e3e5' };
    if (field === 'paymentStatus') s = paymentStatusMap[Number(value)] || s;
    if (field === 'orderStatus') s = orderStatusMap[Number(value)] || s;

    return (
      <span
        style={{
          backgroundColor: s.bg,
          color: s.color,
          padding: '6px 12px',
          borderRadius: '16px',
          fontSize: '11px',
          fontWeight: '700',
          border: `1px solid ${s.color}`,
          display: 'inline-block',
          textAlign: 'center',
          minWidth: '85px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {s.text}
      </span>
    );
  }, []);
  const OrderItemsCellRenderer = useCallback((props) => {
    const orderItems = props.data.orderItems || [];

    // ✅ Exclude refunded items from count
    const activeItems = orderItems.filter((item) => !item.refunded);
    const itemCount = activeItems.length;
    const totalQuantity = activeItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const refundedItems = orderItems.filter((item) => item.refunded).length;
    const voidItems = orderItems.filter((item) => item.voidItem && !item.refunded).length;

    let primaryColor = '#28a745';
    let primaryBg = '#d4edda';
    if (refundedItems === orderItems.length) {
      // All items refunded
      primaryColor = '#dc3545';
      primaryBg = '#f8d7da';
    } else if (voidItems === itemCount) {
      primaryColor = '#856404';
      primaryBg = '#fff3cd';
    } else if (refundedItems > 0 || voidItems > 0) {
      primaryColor = '#fd7e14';
      primaryBg = '#ffeaa7';
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
        <span
          style={{
            backgroundColor: primaryColor,
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            minWidth: '45px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {itemCount} items
        </span>
        <span
          style={{
            backgroundColor: '#E8E2FF',
            color: '#6E5199',
            padding: '4px 6px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: '600',
            minWidth: '35px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Qty: {totalQuantity}
        </span>

        {(refundedItems > 0 || voidItems > 0) && (
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '9px', fontWeight: '600', color: '#6c757d' }}>
            {refundedItems > 0 && (
              <span style={{ backgroundColor: '#dc3545', color: '#fff', padding: '2px 4px', borderRadius: '6px', minWidth: '15px', textAlign: 'center' }}>
                R{refundedItems}
              </span>
            )}
            {voidItems > 0 && (
              <span style={{ backgroundColor: '#856404', color: '#fff', padding: '2px 4px', borderRadius: '6px', minWidth: '15px', textAlign: 'center' }}>
                V{voidItems}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }, []);
  const columnDefs = [
    {
      headerName: '',
      field: 'select',
      checkboxSelection: true,
      headerCheckboxSelection: false,
      width: 50,
      pinned: 'left',
      lockPosition: true,
      cellStyle: { textAlign: 'center' },
      suppressMovable: true,
      suppressResize: true,
      suppressSorting: true,
      suppressFilter: true,
    },
    {
      headerName: 'Order #',
      field: 'displayId',
      filter: true,
      floatingFilter: true,
      minWidth: 100,
      cellStyle: { fontWeight: '700', color: '#6E5199', fontSize: '13px', cursor: 'pointer' },
    },
    {
      headerName: 'Date & Time',
      field: 'orderDate',
      filter: 'agDateColumnFilter',
      minWidth: 170,
      floatingFilter: true,
      valueFormatter: dateFormatter,
      cellStyle: { fontSize: '12px', color: '#2c3e50' },
    },
    {
      headerName: 'Customer',
      field: 'customerName',
      filter: true,
      floatingFilter: true,
      minWidth: 130,
      valueFormatter: (p) => p.value || 'Walk-in',
      cellStyle: (p) => ({ fontStyle: p.value ? 'normal' : 'italic', color: p.value ? '#2c3e50' : '#95a5a6', fontWeight: p.value ? '500' : '400' }),
    },
    { headerName: 'Items', field: 'orderItems', minWidth: 180, cellRenderer: OrderItemsCellRenderer, sortable: false, filter: false },
    {
      headerName: 'Paid Amount',
      field: 'paymentAmount',
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      minWidth: 120,
      valueFormatter: currencyFormatter,
      cellStyle: { fontWeight: '700', color: '#28a745', textAlign: 'right', fontSize: '14px' },
    },
    {
      headerName: 'Payment',
      field: 'paymentStatus',
      filter: true,
      floatingFilter: true,
      cellRenderer: StatusCellRenderer,
      minWidth: 130,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: 'Payment Method',
      field: 'paymentMethods',
      filter: false,
      floatingFilter: false,
      cellRenderer: PaymentMethodsCellRenderer,
      minWidth: 160,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: 'Status',
      field: 'orderStatus',
      filter: true,
      floatingFilter: true,
      minWidth: 130,
      cellRenderer: StatusCellRenderer,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: {
        onPrint: (data) => handlePrintReceipt(data),
        onItemRefund: (data) => handleItemRefund(data),
      },
      minWidth: 140,
      cellStyle: { textAlign: 'center' },
      suppressMovable: true,
      suppressSorting: true,
      suppressFilter: true,
    },
    {
      headerName: 'View',
      field: 'originalId',
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        clicked: (field, data) => handleShow(field, data),
      },
      minWidth: 80,
      pinned: 'right',
      cellStyle: { textAlign: 'center' },
      suppressMovable: true,
    },
  ];

  const ItemSourse = [
    { value: '', label: 'All Orders' },
    { value: '6', label: 'Completed' },
    { value: '8', label: 'On-Hold' },
    { value: '10', label: 'Refunded' },
  ];

  const handleStatusChange = (selectedOption) => {
    setstatus(selectedOption?.value || '');
    setSelectedRows({});
    if (gridRef.current) gridRef.current.api.deselectAll();
  };

  const onBtnCSVExport = useCallback(() => {
    gridRef.current?.api?.exportDataAsCsv({ fileName: `orders_${moment().format('YYYY-MM-DD')}.csv` });
  }, []);
  const onBtExcelExport = useCallback(() => {
    gridRef.current?.api?.exportDataAsExcel({ fileName: `orders_${moment().format('YYYY-MM-DD')}.xlsx` });
  }, []);

  const transformedOrders = useMemo(() => (orderData || []).map(normalizeOrderRow), [orderData]);

  const canRefund =
    selectedRows?.originalId &&
    selectedRows.paymentStatus !== 3 &&
    selectedRows.paymentStatus !== 0 &&
    selectedRows.orderStatus !== 7 &&
    !hasPartialRefunds(selectedRows);

  const canItemRefund =
    selectedRows?.originalId &&
    selectedRows.paymentStatus !== 3 &&
    selectedRows.paymentStatus !== 0 &&
    selectedRows.orderItems &&
    selectedRows.orderItems.length > 0;

  const getPaymentMethodName = (id) => {
    const method = paymentMethod.find((p) => p.id === id);
    return method?.name || 'Cash';
  };

  const needsReference = (methodId) => {
    const method = paymentMethod.find((p) => p.id === methodId);
    if (!method) return false;
    const name = (method.name || '').toLowerCase();
    return name.includes('eft') || name.includes('transfer') || name.includes('account');
  };

  // ✅ For display inside item refund modal, show unique remaining lines (by signature)
  const groupedRefundLines = useMemo(() => {
    const items = selectedRows?.orderItems || [];
    if (!items.length) return [];

    const seen = new Map();

    for (const li of items) {
      if (li.refunded || li.voidItem || isRefundLine(li) || isVoidLine(li)) continue;

      const sig = makeLineKey(li);
      const remaining = getRemainingQuantityForLine(li);
      if (remaining <= 0) continue;

      if (!seen.has(sig)) {
        seen.set(sig, { ...li, lineKey: sig, remainingQty: remaining });
      }
    }

    return Array.from(seen.values());
  }, [selectedRows]);

  return (
    <>
      <button className="addItem-btn m-4" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          padding: '24px 30px',
          background: '#fff',
          borderRadius: '8px',
          marginBottom: '24px',
          boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef',
        }}
      >
        <div className="d-flex gap-4 align-items-center">
          <div>
            <h4 style={{ color: '#495057', fontWeight: '600', margin: 0, fontSize: '24px' }}>Sales History</h4>
            <p style={{ color: '#6c757d', margin: 0, fontSize: '14px' }}>Manage and track all order transactions</p>
          </div>

          <div style={{ minWidth: '280px' }}>
            <Select
              value={ItemSourse.find((item) => item.value === status)}
              onChange={handleStatusChange}
              options={ItemSourse}
              placeholder="Filter by status..."
              isClearable
            />
          </div>

          {loading && (
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm" style={{ color: '#6E5199' }}></div>
              <span style={{ fontSize: '14px', color: '#6c757d', marginLeft: '8px' }}>Loading...</span>
            </div>
          )}
        </div>

        <div className="d-flex gap-3 align-items-center">
          {selectedRows?.id && (
            <div className="d-flex gap-2 align-items-center">
              <span style={{ backgroundColor: '#E8E2FF', color: '#6E5199', padding: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600' }}>
                Selected: {selectedRows?.id}
              </span>

              {canRefund && (
                <button
                  onClick={openRefundModal}
                  className="btn btn-outline-danger btn-sm"
                  style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px' }}
                >
                  Full Refund
                </button>
              )}

              {canItemRefund && (
                <button
                  onClick={() => handleItemRefund(selectedRows)}
                  className="btn btn-sm"
                  style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px', backgroundColor: '#8B7CB6', borderColor: '#8B7CB6', color: '#fff' }}
                >
                  Item Refund
                </button>
              )}
            </div>
          )}

          <div className="d-flex gap-2">
            <button onClick={onBtExcelExport} className="btn btn-outline-success btn-sm" style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px' }}>
              Excel Export
            </button>
            <button
              onClick={onBtnCSVExport}
              className="btn btn-sm"
              style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px', backgroundColor: '#6E5199', borderColor: '#6E5199', color: '#fff' }}
            >
              CSV Export
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="px-5">
        <div className="brdor mt-3">
          <div className="ag-theme-alpine" style={{ height: '80vh' }}>
            <AgGridReact
              ref={gridRef}
              rowSelection="single"
              rowData={transformedOrders}
              immutableData={true}
              getRowId={(params) => String(params.data.originalId)}
              onSelectionChanged={onSelectionChanged}
              suppressRowClickSelection={true}
              animateRows={true}
              enableFilter={true}
              floatingFilter={true}
              columnDefs={columnDefs}
              pagination={true}
              paginationPageSize={25}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              rowHeight={78}
              headerHeight={45}
              suppressCellFocus={true}
            />
          </div>
        </div>
      </div>

      {/* Passcode */}
      {showPasscodePopup && <PasscodePopup onSubmit={handlePasscodeSubmit} onClose={closePasscodePopup} />}

      {/* ✅ FULL REFUND MODAL */}
      <Modal size="md" show={RefundModal} onHide={closeRefundModal} backdrop="static" centered>
        <Modal.Header closeButton style={{ background: '#6E5199', color: '#fff', borderBottom: 'none', borderRadius: '12px 12px 0 0' }}>
          <Modal.Title style={{ fontWeight: '600', fontSize: '18px' }}>💰 Refund Order - {selectedRows?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          {/* Refund Reason */}
          <div className="mb-3">
            <label htmlFor="refundReason" style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>
              Refund Reason *
            </label>
            <Select
              value={REFUND_REASONS.find((r) => r.value === fullRefundReason)}
              onChange={(opt) => setFullRefundReason(opt?.value || '')}
              options={REFUND_REASONS}
              placeholder="Select refund reason..."
            />

            {fullRefundReason === 'other' && (
              <textarea
                className="form-control mt-2"
                placeholder="Please specify refund reason..."
                value={fullRefundNotes}
                onChange={(e) => setFullRefundNotes(e.target.value)}
                rows="3"
              />
            )}
          </div>

          {/* Payment Method Selection */}
          <div className="mb-3">
            <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Refund Payment Method *</label>
            <Select
              value={paymentMethod
                .filter((p) => p.id !== 3)
                .map((p) => ({ value: p.id, label: p.name }))
                .find((o) => o.value === fullRefundPaymentMethod)}
              onChange={(opt) => {
                setFullRefundPaymentMethod(opt?.value || 1);
                setFullRefundReferenceNo('');
              }}
              options={paymentMethod.filter((p) => p.id !== 3).map((p) => ({ value: p.id, label: p.name }))}
              placeholder="Select refund method..."
            />
            <small className="text-muted d-block mt-1">Select how the customer will receive their refund</small>
          </div>

          {/* Reference Number */}
          {needsReference(fullRefundPaymentMethod) && (
            <div className="mb-3">
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Reference Number *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter reference/transaction number..."
                value={fullRefundReferenceNo}
                onChange={(e) => setFullRefundReferenceNo(e.target.value)}
              />
              <small className="text-muted d-block mt-1">Required for {getPaymentMethodName(fullRefundPaymentMethod)} refunds</small>
            </div>
          )}

          {/* Summary */}
          <div className="p-3 bg-warning bg-opacity-10 rounded">
            <div className="d-flex justify-content-between mb-2">
              <span>Refund Amount:</span>
              <strong>
                {Currency?.currency?.code} {Number(selectedRows?.paymentAmount || selectedRows?.orderAmount || 0).toFixed(2)}
              </strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Refund Method:</span>
              <strong>{getPaymentMethodName(fullRefundPaymentMethod)}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '20px 30px', borderTop: '1px solid #f1f3f4' }}>
          <button onClick={closeRefundModal} disabled={loading} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={initiateUpdateOrder}
            disabled={
              loading ||
              !fullRefundReason ||
              (fullRefundReason === 'other' && !fullRefundNotes.trim()) ||
              (needsReference(fullRefundPaymentMethod) && !fullRefundReferenceNo.trim())
            }
            className="btn btn-danger"
          >
            {loading ? 'Processing...' : 'Process Refund'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ✅ ITEM REFUND MODAL */}
      <Modal size="xl" show={ItemRefundModal} onHide={closeItemRefundModal} backdrop="static" centered>
        <Modal.Header
          closeButton
          style={{ background: itemRefundData.isReplacement ? '#8B7CB6' : '#6E5199', color: '#fff', borderBottom: 'none', borderRadius: '12px 12px 0 0' }}
        >
          <Modal.Title style={{ fontWeight: '600', fontSize: '18px' }}>
            {itemRefundData.isReplacement ? '🔄 Item Replacement' : '🔄 Item Refund'} - {selectedRows?.id}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: '30px', maxHeight: '80vh', overflowY: 'auto' }}>
          {/* Toggle */}
          <div className="mb-4">
            <div className="d-flex gap-3 align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="processType"
                  id="refundType"
                  checked={!itemRefundData.isReplacement}
                  onChange={() => handleReplacementToggle(false)}
                />
                <label className="form-check-label" htmlFor="refundType">
                  Refund Items
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="processType"
                  id="replacementType"
                  checked={itemRefundData.isReplacement}
                  onChange={() => handleReplacementToggle(true)}
                />
                <label className="form-check-label" htmlFor="replacementType">
                  Replace Items
                </label>
              </div>
            </div>
          </div>

          {/* Items Selection */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 style={{ margin: 0 }}>Select Items</h6>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-primary" onClick={handleSelectAllItems}>
                  Select All Available
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleDeselectAllItems}
                  disabled={itemRefundData.selectedItems.length === 0}
                >
                  Clear All
                </button>
              </div>
            </div>

            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '15px' }}>
              {groupedRefundLines.map((item, index) => {
                const selected = isLineSelected(item);
                const selectedQty = getSelectedLineQuantity(item);
                const remainingQty = getRemainingQuantityForLine(item);
                const refundHistory = getRefundHistoryText(item.itemId);
                const isDisabled = remainingQty === 0;

                return (
                  <div
                    key={`${item.itemId}-${item.itemIndex ?? index}`}
                    className="d-flex align-items-center justify-content-between p-3 mb-2"
                    style={{
                      background: isDisabled ? '#f8f9fa' : selected ? '#E8E2FF' : '#fff',
                      borderRadius: '8px',
                      border: '2px solid',
                      borderColor: isDisabled ? '#e9ecef' : selected ? '#6E5199' : '#e9ecef',
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <div className="d-flex align-items-center flex-grow-1">
                      <input
                        type="checkbox"
                        className="form-check-input me-3"
                        checked={selected}
                        onChange={(e) => handleItemSelection(item, e.target.checked)}
                        disabled={isDisabled}
                      />
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: 600 }}>
                          {item.menuItemName || item.itemName || item.itemDescription || `Item ${item.itemId}`}
                          {item.refunded && <span style={{ color: '#dc3545', marginLeft: 8 }}>[REFUNDED]</span>}
                          {item.voidItem && <span style={{ color: '#856404', marginLeft: 8 }}>[VOID]</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#6c757d' }}>
                          Remaining: <b>{remainingQty}</b> | Price: {Currency?.currency?.code}
                          {Number(item.menuItemPrice || 0).toFixed(2)}
                        </div>
                        {refundHistory && <div style={{ fontSize: 11, color: '#fd7e14', fontWeight: 600 }}>⚠️ {refundHistory}</div>}
                      </div>
                    </div>

                    {selected && remainingQty > 0 && (
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min="1"
                          max={remainingQty}
                          value={selectedQty}
                          onChange={(e) => handleItemQuantityChange(item, e.target.value)}
                          style={{ width: 90, textAlign: 'center', fontWeight: 600 }}
                        />
                        <div style={{ fontWeight: 700, minWidth: 90, textAlign: 'right' }}>
                          {Currency?.currency?.code}
                          {(Number(item.menuItemPrice || 0) * Math.min(Number(selectedQty), remainingQty)).toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Replacement Items */}
          {itemRefundData.isReplacement && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 style={{ color: '#495057', fontWeight: '600', margin: 0 }}>➕ Replacement Items</h6>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={addReplacementItem}
                  style={{ fontSize: '12px', padding: '4px 12px', backgroundColor: '#8B7CB6', borderColor: '#8B7CB6', color: '#fff' }}
                >
                  + Add Item
                </button>
              </div>

              <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '15px' }}>
                {itemRefundData.replacementItems.map((item) => (
                  <div key={item.id} className="mb-3 p-3" style={{ background: '#E8E2FF', borderRadius: '8px', border: '1px solid #8B7CB6' }}>
                    <div className="row">
                      <div className="col-md-5">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>
                          Select Menu Item *
                        </label>
                        <Select
                          value={item.selectedMenuItem}
                          onChange={(opt) => updateReplacementItem(item.id, 'selectedMenuItem', opt)}
                          options={menuItems}
                          placeholder="Search and select item..."
                          isSearchable
                          isClearable
                          isLoading={loadingMenuItems}
                        />
                      </div>
                      <div className="col-md-2">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>Price</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={
                            item.selectedMenuItem
                              ? `${Currency?.currency?.code}${Number(item.selectedMenuItem.salePrice || 0).toFixed(2)}`
                              : `${Currency?.currency?.code}0.00`
                          }
                          disabled
                          style={{ backgroundColor: '#f8f9fa', fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-2">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>Qty *</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.quantity}
                          onChange={(e) => updateReplacementItem(item.id, 'quantity', e.target.value)}
                          placeholder="1"
                          min="1"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-2">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>Notes</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.notes}
                          onChange={(e) => updateReplacementItem(item.id, 'notes', e.target.value)}
                          placeholder="Optional notes"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-1 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeReplacementItem(item.id)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {itemRefundData.replacementItems.length === 0 && (
                  <div className="text-center py-3">
                    <span style={{ color: '#6c757d', fontSize: '14px' }}>No replacement items added</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ✅ Payment Method Section for Item Refund */}
          {!itemRefundData.isReplacement && calculateNetRefundAmount() > 0 && (
            <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <h6 className="mb-3" style={{ fontWeight: '600' }}>
                Refund Payment Details
              </h6>

              <div className="mb-3">
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Refund Payment Method *</label>
                <Select
                  value={paymentMethod
                    .filter((p) => p.id !== 3)
                    .map((p) => ({ value: p.id, label: p.name }))
                    .find((o) => o.value === refundPaymentMethod)}
                  onChange={(opt) => handleRefundPaymentMethodChange(opt?.value || 1)}
                  options={paymentMethod.filter((p) => p.id !== 3).map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Select refund method..."
                />
                <small className="text-muted d-block mt-1">Select how the customer will receive their refund</small>
              </div>

              {needsReference(refundPaymentMethod) && (
                <div className="mb-3">
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Reference Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter reference/transaction number..."
                    value={refundReferenceNo}
                    onChange={(e) => setRefundReferenceNo(e.target.value)}
                  />
                  <small className="text-muted d-block mt-1">Required for {getPaymentMethodName(refundPaymentMethod)} refunds</small>
                </div>
              )}

              {refundPaymentMethod === 1 && (
                <div className="row">
                  <div className="col-md-6">
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Cash Amount Given</label>
                    <input
                      type="number"
                      className="form-control"
                      value={refundAmountCollected}
                      onChange={(e) => handleRefundAmountChange(parseFloat(e.target.value) || 0)}
                      placeholder="Enter cash amount"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="col-md-6">
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Change</label>
                    <input type="text" className="form-control" value={`${refundChange.toFixed(2)}`} disabled style={{ backgroundColor: '#e9ecef' }} />
                    {refundChange > 0 && <small style={{ color: '#28a745', fontWeight: '600' }}>Change to return: {refundChange.toFixed(2)}</small>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="mb-4">
            <label style={{ fontWeight: 600 }}>{itemRefundData.isReplacement ? 'Replacement' : 'Refund'} Reason *</label>
            <textarea
              className="form-control"
              name="refundReason"
              value={itemRefundData.refundReason}
              onChange={(e) => setItemRefundData((p) => ({ ...p, refundReason: e.target.value }))}
              rows="3"
              placeholder="Enter reason for refund/replacement..."
            />
          </div>

          {/* Summary */}
          {itemRefundData.selectedItems.length > 0 && (
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12 }}>
              <div>
                Items value:{' '}
                <b>
                  {Currency?.currency?.code}
                  {calculateItemRefundAmount().toFixed(2)}
                </b>
              </div>
              {itemRefundData.isReplacement && (
                <div>
                  Replacement value:{' '}
                  <b>
                    {Currency?.currency?.code}
                    {calculateReplacementAmount().toFixed(2)}
                  </b>
                </div>
              )}
              <div className="mt-2 pt-2 border-top">
                <strong>Net refund: </strong>
                <b style={{ fontSize: '18px', color: '#dc3545' }}>
                  {Currency?.currency?.code}
                  {calculateNetRefundAmount().toFixed(2)}
                </b>
              </div>
              {!itemRefundData.isReplacement && (
                <div className="mt-2">
                  <small className="text-muted">
                    Refund via: <strong>{getPaymentMethodName(refundPaymentMethod)}</strong>
                  </small>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button onClick={closeItemRefundModal} disabled={itemRefundLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={executeItemRefund} disabled={itemRefundLoading} className="btn btn-primary">
            {itemRefundLoading ? 'Processing...' : itemRefundData.isReplacement ? 'Replace Items' : 'Refund Items'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Print Modal */}
      <Modal size="lg" show={PrintModal} onHide={closePrintModal} backdrop="static" centered>
        <Modal.Header closeButton style={{ background: '#6E5199', color: '#fff' }}>
          <Modal.Title>🖨️ Print Receipt - {selectedRows?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px 30px' }}>
          <div style={{ borderBottom: '1px dashed #dee2e6', paddingBottom: '12px', marginBottom: '12px' }}>
            <div className="d-flex justify-content-between mb-1">
              <span style={{ color: '#6c757d' }}>Order #:</span>
              <b>{selectedRows?.displayId || `ORD-${selectedRows?.id}`}</b>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span style={{ color: '#6c757d' }}>Invoice #:</span>
              <b>{selectedRows?.invoiceNumber || '-'}</b>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span style={{ color: '#6c757d' }}>Date:</span>
              <b>{selectedRows?.orderDate ? moment(selectedRows.orderDate).format('DD/MM/YYYY HH:mm') : '-'}</b>
            </div>
          </div>

          <div style={{ borderBottom: '1px dashed #dee2e6', paddingBottom: '12px', marginBottom: '12px' }}>
            <div className="d-flex justify-content-between mb-1">
              <span style={{ color: '#6c757d' }}>Order Total:</span>
              <b>
                {Currency?.currency?.code} {Number(selectedRows?.orderAmount || 0).toFixed(2)}
              </b>
            </div>
            {Number(selectedRows?.discount || 0) > 0 && (
              <div className="d-flex justify-content-between mb-1">
                <span style={{ color: '#0a7a2f' }}>Discount:</span>
                <b style={{ color: '#0a7a2f' }}>
                  - {Currency?.currency?.code} {Number(selectedRows?.discount || 0).toFixed(2)}
                </b>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px', color: '#495057' }}>Payment Breakdown:</div>

            {Array.isArray(selectedRows?.payments) && selectedRows.payments.length > 0 ? (
              selectedRows.payments.map((p, i) => (
                <div key={i} className="d-flex justify-content-between mb-1">
                  <span style={{ color: '#6c757d', textTransform: 'capitalize' }}>{p.method}:</span>
                  <b style={{ color: '#28a745' }}>
                    {Currency?.currency?.code} {Number(p.amount ?? p.paymentAmount ?? 0).toFixed(2)}
                  </b>
                </div>
              ))
            ) : (
              <div className="d-flex justify-content-between mb-1">
                <span style={{ color: '#6c757d' }}>{selectedRows?.paymentMethod || 'Payment'}:</span>
                <b style={{ color: '#28a745' }}>
                  {Currency?.currency?.code} {Number(selectedRows?.paymentAmount || 0).toFixed(2)}
                </b>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={closePrintModal} disabled={printLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={executeBrowserPrint} disabled={printLoading} className="btn btn-outline-primary">
            {printLoading ? 'Printing...' : 'Print'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// View button
class BtnCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.btnClickedHandler = this.btnClickedHandler.bind(this);
  }
  btnClickedHandler() {
    const { value, clicked, data } = this.props;
    clicked(value, data);
  }
  render() {
    const { paymentStatus } = this.props.data;
    const isPending = paymentStatus === 0;
    return (
      <button
        onClick={this.btnClickedHandler}
        className={isPending ? 'AddBtn' : 'btn btn-outline-primary'}
        style={{ fontSize: '12px', padding: '4px 12px', fontWeight: '500', borderRadius: '4px', minWidth: '70px' }}
      >
        {isPending ? 'Proceed' : 'View'}
      </button>
    );
  }
}

// Actions: Print + Item refund
class ActionsCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.handlePrint = this.handlePrint.bind(this);
    this.handleItemRefund = this.handleItemRefund.bind(this);
  }
  handlePrint() {
    const { onPrint, data } = this.props;
    onPrint(data);
  }
  handleItemRefund() {
    const { onItemRefund, data } = this.props;
    onItemRefund(data);
  }
  render() {
    const { data } = this.props;
    const canItemRefund = data.paymentStatus !== 3 && data.paymentStatus !== 0 && data.orderItems && data.orderItems.length > 0;
    return (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <button
          onClick={this.handlePrint}
          className="btn btn-outline-success btn-sm"
          style={{ fontSize: '11px', padding: '4px 8px', fontWeight: '500', borderRadius: '4px', minWidth: '50px' }}
          title="Print Receipt"
        >
          🖨️
        </button>
        {canItemRefund && (
          <button
            onClick={this.handleItemRefund}
            className="btn btn-sm"
            style={{
              fontSize: '11px',
              padding: '4px 8px',
              fontWeight: '500',
              borderRadius: '4px',
              minWidth: '50px',
              backgroundColor: '#8B7CB6',
              borderColor: '#8B7CB6',
              color: '#fff',
            }}
            title="Refund Items"
          >
            🔄
          </button>
        )}
      </div>
    );
  }
}

const connectMapStateToProps = (state) => ({
  Currency: state.Currency,
  paymentMethod: state.paymentMethod,
  menuItems: state.menuItems,
});

export default connect(connectMapStateToProps)(Order);
